import type {Component, Editor} from 'grapesjs';
import type {CropRect} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';
import {CropWindowHandle} from '@EmailMakerReact/lib/email-maker-image-crop-geometry';

export interface ImageCropOverlayProps {
  editor: Editor;
}

export interface OverlayBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ImageCropOverlayGeometry {
  frameBox: OverlayBox;
  frameClipBox: OverlayBox;
  imageBox: OverlayBox;
}

export interface MeasuredImageCropOverlayGeometry {
  component: Component;
  geometry: ImageCropOverlayGeometry;
}

export interface ImageCropOverlayRects {
  layerRect: DOMRect;
  frameRect: DOMRect;
  frameClientLeft: number;
  frameClientTop: number;
  frameClientWidth: number;
  frameClientHeight: number;
  imageRect: DOMRect;
}

export interface ClientPoint {
  x: number;
  y: number;
}

export interface NaturalPoint {
  x: number;
  y: number;
}

export enum CropHandlePosition {
  TOP_LEFT = 'topLeftHandle',
  TOP = 'topHandle',
  TOP_RIGHT = 'topRightHandle',
  RIGHT = 'rightHandle',
  BOTTOM_RIGHT = 'bottomRightHandle',
  BOTTOM = 'bottomHandle',
  BOTTOM_LEFT = 'bottomLeftHandle',
  LEFT = 'leftHandle',
}

export interface CropWindowHandleSpec {
  handle: CropWindowHandle;
  position: CropHandlePosition;
  fractionX: number;
  fractionY: number;
  isCorner: boolean;
}

const HANDLE_START_FRACTION = 0;
const HANDLE_MIDDLE_FRACTION = 0.5;
const HANDLE_END_FRACTION = 1;

export const CROP_WINDOW_HANDLE_SPECS: CropWindowHandleSpec[] = [
  {handle: CropWindowHandle.TOP_LEFT, position: CropHandlePosition.TOP_LEFT, fractionX: HANDLE_START_FRACTION, fractionY: HANDLE_START_FRACTION, isCorner: true},
  {handle: CropWindowHandle.TOP_RIGHT, position: CropHandlePosition.TOP_RIGHT, fractionX: HANDLE_END_FRACTION, fractionY: HANDLE_START_FRACTION, isCorner: true},
  {handle: CropWindowHandle.BOTTOM_RIGHT, position: CropHandlePosition.BOTTOM_RIGHT, fractionX: HANDLE_END_FRACTION, fractionY: HANDLE_END_FRACTION, isCorner: true},
  {handle: CropWindowHandle.BOTTOM_LEFT, position: CropHandlePosition.BOTTOM_LEFT, fractionX: HANDLE_START_FRACTION, fractionY: HANDLE_END_FRACTION, isCorner: true},
  {handle: CropWindowHandle.TOP, position: CropHandlePosition.TOP, fractionX: HANDLE_MIDDLE_FRACTION, fractionY: HANDLE_START_FRACTION, isCorner: false},
  {handle: CropWindowHandle.RIGHT, position: CropHandlePosition.RIGHT, fractionX: HANDLE_END_FRACTION, fractionY: HANDLE_MIDDLE_FRACTION, isCorner: false},
  {handle: CropWindowHandle.BOTTOM, position: CropHandlePosition.BOTTOM, fractionX: HANDLE_MIDDLE_FRACTION, fractionY: HANDLE_END_FRACTION, isCorner: false},
  {handle: CropWindowHandle.LEFT, position: CropHandlePosition.LEFT, fractionX: HANDLE_START_FRACTION, fractionY: HANDLE_MIDDLE_FRACTION, isCorner: false},
];

export enum CropWindowDragKind {
  MOVE = 'move',
  RESIZE = 'resize',
}

export interface CropWindowDragStart {
  pointerId: number;
  startPointer: ClientPoint;
  startCropWindow: CropRect;
  scale: number;
}

export interface CropWindowMoveDrag extends CropWindowDragStart {
  kind: CropWindowDragKind.MOVE;
}

export interface CropWindowResizeDrag extends CropWindowDragStart {
  kind: CropWindowDragKind.RESIZE;
  spec: CropWindowHandleSpec;
}

export type CropWindowDrag = CropWindowMoveDrag | CropWindowResizeDrag;
